import ExcelJS from 'exceljs';

/**
 * Populates the ECCO Cloud Support Model V2.8 Factors sheet with the current
 * calculator state and triggers a browser download of the updated workbook.
 *
 * Only INPUT cells (no formula) in the Factors sheet are written. Formula-driven
 * cells (B33=B29, B46, B48, B49, B50, B51, B52, B53) are left intact so Excel
 * recalculates them correctly on open.
 */
export async function exportEccoToExcel({ projectInfo, tier, wx, config, addOns, channels, rates }) {
  // Fetch the template from the public folder (same pattern as AWS excelExporter)
  const templateUrl = process.env.PUBLIC_URL + '/satools_files/ECCO_Cloud_Support_Model_V2.8.xlsx';
  const response = await fetch(templateUrl);
  if (!response.ok) throw new Error(`Failed to load template: ${response.statusText}`);
  const arrayBuffer = await response.arrayBuffer();

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);

  const f = wb.getWorksheet('Factors');
  if (!f) throw new Error('Factors sheet not found in template');

  const set = (addr, val) => {
    const cell = f.getCell(addr);
    // Clear any existing formula so our value takes effect
    cell.value = val === null || val === undefined || val === '' ? null : val;
  };

  // ── Project / Team Info ──────────────────────────────────────────────────────
  set('B11', projectInfo.saName   || null);
  set('B12', projectInfo.customerName || null);
  set('B13', projectInfo.customerName || null);   // Project Name = Customer Name
  set('B15', projectInfo.amName   || null);
  set('B16', projectInfo.bdmName  || null);
  set('B19', projectInfo.oppNum   || null);

  // ── Managed Services Config ──────────────────────────────────────────────────
  set('B28', projectInfo.platform || 'WxCC');
  set('B29', config.agents  || null);
  set('B30', config.ai      || null);
  set('B31', config.emailEP || null);
  set('B32', config.chatEP  || null);

  // B33 = IVR Ports. The template formula is =B29 (inherits agent count).
  // Only override if the user has manually set a different value.
  if (config.ivrPortsManual) {
    set('B33', config.ivrPorts || null);
  }
  // otherwise leave the =B29 formula intact

  set('B34', config.dialerPorts || null);

  // B35 = Webex Calling dropdown (Cloud Select / Cloud Reactive / blank for None)
  set('B35', wx === 'None' ? null : wx);

  set('B36', tier);
  set('B37', projectInfo.contractTerm || 12);
  set('B38', projectInfo.contractType || 'New');

  // ── Internal Rates ───────────────────────────────────────────────────────────
  // B39 = CC Delivery Center Eng Cost (input cell, no formula)
  set('B39', rates.dcEngCost ?? 75);
  // B40 = Break Fix hrs / ticket = 2 (constant, leave)
  // B41 = MACD hrs / ticket = 1 (constant, leave)

  // ── Add-Ons / Counts ─────────────────────────────────────────────────────────
  set('B42', config.custTickets   || null);
  set('B43', addOns.electiveHours || null);
  set('B44', rates.esrRate ?? 195);

  // B45: Synthetic Transactions Setup Fee — formula IF(B36="Cloud Select Plus",500,0)
  // Override only if user has changed the rate from default
  if ((rates.synthSetupFee ?? 500) !== 500) {
    set('B45', rates.synthSetupFee);
  }
  // B46: =IF(B57<1,0,B57*500) — leave formula

  // B47: Synthetic Transactions Cost — formula IF(B36="Cloud Select Plus",1000,0)
  if ((rates.synthAnnualCost ?? 1000) !== 1000) {
    set('B47', rates.synthAnnualCost);
  }
  // B48: =B57*1000 — leave formula

  // B49: Cloud Onboarding — formula IF(OR(B28="Five9",B28="Genesys"),800,500)
  // Leave as formula (depends on B28 which we set above)

  // B50: Vigilus ServiceNow — formula IF(OR(B36="Cloud Select Plus",B56>0),1000,0)
  if ((rates.vigilusServiceNowCost ?? 1000) !== 1000) {
    set('B50', rates.vigilusServiceNowCost);
  }
  // B51: =B58*200 — leave formula
  // B52: =IF(B36="Cloud Select Plus",10000,0) — leave formula
  // B53: =B56*10000 — leave formula

  // ── Agentless Channel Quantities ─────────────────────────────────────────────
  const advQty = channels
    .filter(c => c.category === 'Advanced')
    .reduce((s, c) => s + (parseFloat(c.qty) || 1), 0);
  const basQty = channels
    .filter(c => c.category === 'Basic')
    .reduce((s, c) => s + (parseFloat(c.qty) || 1), 0);

  set('B54', advQty || null);
  set('B55', basQty || null);
  set('D54', channels.filter(c => c.category === 'Advanced').map(c => c.name).filter(Boolean).join(', ') || null);
  set('D55', channels.filter(c => c.category === 'Basic').map(c => c.name).filter(Boolean).join(', ') || null);
  set('B56', addOns.annualMinutes || null);
  set('B57', addOns.synthCount    || null);
  set('B58', addOns.vigilusSessions || null);
  set('B59', config.thirdParty   || null);
  set('D59', config.thirdPartyNotes || null);

  // ── Channel Details sheet ────────────────────────────────────────────────────
  const existingChSheet = wb.getWorksheet('Channel Details');
  if (existingChSheet) wb.removeWorksheet(existingChSheet.id);
  const chSheet = wb.addWorksheet('Channel Details');
  chSheet.columns = [
    { header: '#',                 key: 'id',       width: 5 },
    { header: 'Name',              key: 'name',     width: 24 },
    { header: 'Types',             key: 'types',    width: 20 },
    { header: 'Category',          key: 'category', width: 12 },
    { header: 'Qty',               key: 'qty',       width: 8 },
    { header: 'Platform',          key: 'platform', width: 16 },
    { header: 'Deployment',        key: 'deployment', width: 14 },
    { header: 'Integration Platform', key: 'integration', width: 22 },
    { header: 'Business Objective', key: 'businessObjective', width: 40 },
    { header: 'Target Use Cases',  key: 'useCases', width: 40 },
  ];
  chSheet.getRow(1).font = { bold: true };
  (channels || []).forEach(ch => {
    chSheet.addRow({
      id: ch.id,
      name: ch.name || null,
      types: (ch.types || []).join(', ') || null,
      category: ch.category || null,
      qty: parseFloat(ch.qty) || 1,
      platform: ch.platform || null,
      deployment: ch.deployment || null,
      integration: ch.integration || null,
      businessObjective: ch.businessObjective || null,
      useCases: ch.useCases || null,
    });
  });
  chSheet.getColumn('businessObjective').alignment = { wrapText: true, vertical: 'top' };
  chSheet.getColumn('useCases').alignment = { wrapText: true, vertical: 'top' };

  // ExcelJS doesn't reliably round-trip Excel Tables (ListObjects) — rewriting this
  // workbook corrupts the "PS Part #s" sheet's table/AutoFilter definition, which
  // triggers Excel's "file level validation and repair" prompt on open. That table
  // is a static hidden lookup sheet the app never reads, so drop it before writing.
  const psSheet = wb.getWorksheet('PS Part #s');
  if (psSheet) {
    psSheet.tables && Object.keys(psSheet.tables).forEach(name => psSheet.removeTable(name));
  }

  // ── Build filename and trigger download ──────────────────────────────────────
  const customer = (projectInfo.customerName || 'Customer').replace(/[^a-z0-9]/gi, '_');
  const now      = new Date();
  const pad      = n => String(n).padStart(2, '0');
  const datePart = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}`;
  const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}`;
  const filename = `ECCO_${customer}_${datePart}_${timePart}.xlsx`;

  const buffer = await wb.xlsx.writeBuffer();
  const blob   = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a   = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Reads a user-supplied ECCO workbook (.xlsx), parses the Factors sheet, and
 * returns a state patch object the component can spread into its setState calls.
 *
 * Only cells that have a non-null/non-empty value are included in the patch so
 * the caller can decide how to merge with existing defaults.
 */
export async function importEccoFromExcel(file) {
  const arrayBuffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);

  const f = wb.getWorksheet('Factors');
  if (!f) throw new Error('This file does not contain a "Factors" sheet. Please upload an ECCO Model V2.8 workbook.');

  const val = addr => {
    const cell = f.getCell(addr);
    const v = cell.value;
    if (v === null || v === undefined) return null;
    // Formula cells expose their computed result in .result
    if (typeof v === 'object' && v.result !== undefined) return v.result;
    if (typeof v === 'object' && v.formula) return null; // uncomputed formula — skip
    return v;
  };

  const num  = addr => { const v = val(addr); return (v !== null && !isNaN(+v)) ? +v : null; };
  const str  = addr => { const v = val(addr); return v !== null ? String(v).trim() : null; };

  // ── Project / Team Info ──────────────────────────────────────────────────────
  const projectInfo = {};
  const saName   = str('B11'); if (saName)   projectInfo.saName   = saName;
  const custName = str('B12'); if (custName)  projectInfo.customerName = custName;
  const amName   = str('B15'); if (amName)   projectInfo.amName   = amName;
  const bdmName  = str('B16'); if (bdmName)  projectInfo.bdmName  = bdmName;
  const oppNum   = str('B19'); if (oppNum)   projectInfo.oppNum   = oppNum;
  const platform = str('B28'); if (platform) projectInfo.platform = platform;
  const term     = num('B37'); if (term)     projectInfo.contractTerm = term;
  const ctype    = str('B38'); if (ctype)    projectInfo.contractType = ctype;

  // ── Tier / Webex ─────────────────────────────────────────────────────────────
  const tier = str('B36') || null;
  const wxRaw = str('B35');
  const wx = wxRaw || 'None';

  // ── Agent Config ─────────────────────────────────────────────────────────────
  const config = {};
  const agents      = num('B29'); if (agents      !== null) config.agents      = agents;
  const ai          = num('B30'); if (ai          !== null) config.ai          = ai;
  const emailEP     = num('B31'); if (emailEP     !== null) config.emailEP     = emailEP;
  const chatEP      = num('B32'); if (chatEP      !== null) config.chatEP      = chatEP;
  const dialerPorts = num('B34'); if (dialerPorts !== null) config.dialerPorts = dialerPorts;
  const thirdParty  = num('B59'); if (thirdParty  !== null) config.thirdParty  = thirdParty;
  const custTickets = num('B42'); if (custTickets !== null) config.custTickets = custTickets;

  // IVR Ports — if cell value differs from agents, treat as manual override
  const ivrRaw = num('B33');
  if (ivrRaw !== null) {
    const agentCount = num('B29') ?? 0;
    config.ivrPorts = ivrRaw;
    config.ivrPortsManual = ivrRaw !== agentCount;
  }

  // ── Add-Ons ──────────────────────────────────────────────────────────────────
  const addOns = {};
  const electiveHours    = num('B43'); if (electiveHours    !== null) addOns.electiveHours    = electiveHours;
  const vigilusSessions  = num('B58'); if (vigilusSessions  !== null) addOns.vigilusSessions  = vigilusSessions;
  const synthCount       = num('B57'); if (synthCount       !== null) addOns.synthCount       = synthCount;
  const annualMinutes    = num('B56'); if (annualMinutes    !== null) addOns.annualMinutes    = annualMinutes;

  // ── Rates ─────────────────────────────────────────────────────────────────────
  const rates = {};
  const dcEngCost  = num('B39'); if (dcEngCost  !== null) rates.dcEngCost  = dcEngCost;
  const esrRate    = num('B44'); if (esrRate    !== null) rates.esrRate    = esrRate;
  // Conditional rate overrides (only pick up if the cell is a plain number, not a formula)
  const synthSetup = num('B45'); if (synthSetup !== null) rates.synthSetupFee     = synthSetup;
  const synthCost  = num('B47'); if (synthCost  !== null) rates.synthAnnualCost   = synthCost;
  const snCost     = num('B50'); if (snCost     !== null) rates.vigilusServiceNowCost = snCost;

  // ── Agentless Channels ────────────────────────────────────────────────────────
  // B54 = Advanced channel qty, B55 = Basic channel qty. Column D on those rows
  // (D54/D55) holds a comma-separated list of channel names, matching the "Notes"
  // convention used elsewhere in this column (e.g. D44, D57, D59). We build one
  // channel entry per unit of qty, named from that list (falling back to a generic
  // "Advanced/Basic Agentless Channel N" name for any unit without a matching name).
  const advQty  = num('B54') ?? 0;
  const basQty  = num('B55') ?? 0;

  const parseNames = addr => {
    const raw = str(addr);
    return raw ? raw.split(/[,;\n]/).map(s => s.trim()).filter(Boolean) : [];
  };
  const advNames = parseNames('D54');
  const basNames = parseNames('D55');

  let nextId = 1;
  const buildChannels = (qty, names, category) => {
    const list = [];
    for (let i = 0; i < qty; i++) {
      list.push({
        id: nextId++,
        name: names[i] || `${category} Agentless Channel ${i + 1}`,
        types: ['Voice'],
        category,
        qty: 1,
        platform: '',
        deployment: 'Cloud',
        integration: '',
        businessObjective: '',
        useCases: '',
        languages: 'English',
        notes: '',
      });
    }
    return list;
  };
  const channels = [
    ...buildChannels(advQty, advNames, 'Advanced'),
    ...buildChannels(basQty, basNames, 'Basic'),
  ];

  return { projectInfo, tier, wx, config, addOns, rates, advQty, basQty, channels };
}
